import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ParsedGameData} from '@nolimitcity/slot-game/bin/core/server/data/ParsedGameData';
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite} from "gsap";
import {BarbarianFuryGameConfig} from "../../BarbarianFuryGameConfig";
import {BarbarianFurySoundConfig} from '../../BarbarianFurySoundConfig';
import {BarbarianFuryGameMode} from '../../gamemode/BarbarianFuryGameMode';
import {BFGameShaker} from "../../gameshake/BFGameShaker";
import {BarbarianFuryParsedGameData} from "../../server/data/BarbarianFuryParsedGameData";

export class BFNudgeStackedWPView extends BaseView {
    private _nudgeData:any;
    private _steps:number[] = [];
    private _frozenSymLayer:ScalableLayer;
    private _data:ParsedGameData;
    private _freezeAnims:TimelineSprite[] = [];
    private _freezeSmokeAnims:TimelineSprite[] = [];
    private _removingFreezeAnims:TimelineSprite[] = [];
    private _respinGridExplodePlayed:boolean = false;
    private _isRestore:boolean = false;
    private _initDataReceived:boolean = false;
    private _resourcesLoaded:boolean = false;
    private _restoreBardata:boolean[][] = [];
    public _changeSymState:number[][] = []; //  create this variable to store stack position while teasing.

    constructor() {
        super();
        this._frozenSymLayer = StageManager.getLayer(BarbarianFuryGameConfig.instance.LAYERS.FROZEN_SYMBOL.name);
    }

    protected addEventListeners():void {
        super.addBaseEventListeners();
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    protected onInitDataParsed(data:BarbarianFuryParsedGameData):void {
        this._data = data;
        this._initDataReceived = true;
        this._isRestore = data.isRestoreState;
        let _resoreBarData:boolean[][] = [];
        _resoreBarData.push(data!.initialStackOverlay, data!.overlayWildInRespin);
        this._restoreBardata = _resoreBarData;
        //  added this check to only show bars when there is restre & gamemode is respin
        if(data.isRestoreState && this._resourcesLoaded && (data.nextMode === BarbarianFuryGameMode.STACKED_RESPIN || data.nextMode === BarbarianFuryGameMode.STACKED_RESPIN_FREESPIN)) {
            this.showBarsOnRestore(_resoreBarData);
        }
    }

    public performTease(reelId:number, stackedSymbolIndex:number, steps:number, stackedSpinTriggered:boolean):void {
        let nudgeUp:Boolean = steps >= 0;
        if(steps === 0) {
            EventHandler.dispatchEvent(new GameEvent("reelTeaseCompleted", reelId));
        } else {
            SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.RESPIN_TEASE_DOWN);
            EventHandler.dispatchEvent(new GameEvent("reelTeaseStart", reelId, nudgeUp, stackedSpinTriggered));
        }
    }

    public teaseReels(nudgeData:any):void {
        this._changeSymState = [];
        this._nudgeData = nudgeData;
        this._nudgeData!.initialStackOverlay.forEach((doTease:boolean, reelId:number) => {
            if(doTease) {
                this.doTease(reelId);
            }
        });
    }

    public playFreezingAnimations(data:any):void {
        this._respinGridExplodePlayed = false;
        SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.RESPIN_GRID_CLOSE);
        for(let i:number = 0; i < this._freezeAnims.length; i++) {
            if(data!.initialStackOverlay[i] || data!.overlayWildInRespin[i]) {
                const timeline:TimelineLite = new TimelineLite({paused : true});
                timeline.add(() => {
                    this._freezeAnims[i].getAnimationAutoShowHide(true, false, [20], () => {
                        if(this._freezeAnims[i].currentFrame === 8) {
                            this._freezeSmokeAnims[i].getAnimationAutoShowHide(true, true);
                        }
                    });
                }, 0);
                timeline.play(0);
            }
            EventHandler.dispatchEvent(new GameEvent(BFGameShaker.STOP_GAME_SHAKING));
        }
    }

    /**
     * function to show bars on restore
     * M symbols stack, wild stack.
     * @param restoreBarsData
     */
    private showBarsOnRestore(restoreBarsData:boolean[][]) {
        for(let reelIds of restoreBarsData) {
            if(!reelIds) {
                reelIds = this.getReelIdsOfWildStack(restoreBarsData[0]);
            }
            for(let i = 0; i < reelIds.length; i++) {
                if(reelIds[i]) {
                    if(!this._freezeAnims[i]) {
                        // #153 #168 making sure to create anims before using them.
                        for(let i:number = 0; i < BarbarianFuryGameConfig.instance.REELS_NUM; i++) {
                            this.createFreezeAnimation(i);
                        }
                    }
                    this._freezeAnims[i].visible = true;
                    this._freezeAnims[i].setFrame(20);
                }
            }
        }
    }

    /**
     * function to get wild stack on init game
     * @param reelId
     */
    private getReelIdsOfWildStack(reelId:boolean[]):boolean[] {
        for(let i = 0; i < reelId.length; i++) {
            if(!reelId[i]) {
                if((this._data.reels[i][1] === "W*0") && (this._data.reels[i][2] === "W*1") && (this._data.reels[i][3] === "W*2") && (this._data.reels[i][4] === "W*3")) {
                    reelId[i] = true;
                }
            }
        }
        return reelId;
    }

    public stopFreezingAnimations(stackedPos:boolean[]):void {
        !this._respinGridExplodePlayed && SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.RESPIN_GRID_EXPLODE);
        for(let i:number = 0; i < this._freezeAnims.length; i++) {
            if(this._freezeAnims[i].visible) {
                this._freezeAnims[i].hide();
                if(stackedPos[i]) {
                    this._removingFreezeAnims[i].getAnimationAutoShowHide(true, true);
                }
            }
        }
        this._respinGridExplodePlayed = true;
    }

    protected doTease(reelId:number):boolean {
        let reelStr:string = this._nudgeData!.reels[reelId].toString();
        let stackedSymbol:string = reelStr.substring(reelStr.indexOf("*") - 2, reelStr.indexOf("*"));
        let stackedSymbolIndex = parseInt(reelStr.substr(reelStr.indexOf("*") + 1, 1));
        let stackedSpinTriggered = this._nudgeData!.stackedSpinTriggered;
        const symbols:string[] = this._nudgeData!.reels[reelId];
        let index:number = symbols.indexOf(`${stackedSymbol}*${stackedSymbolIndex.toString()}`);
        let sym = Reels.getSymbol(reelId, index);
        sym!.changeState({
            state : BarbarianFuryGameConfig.instance.SYMBOL_STATES.win
        });
        this._changeSymState.push([reelId, index]);
        if(MathHelper.isInRange(index, 0, 5)) {
            this._steps[reelId] = stackedSymbolIndex + 1 - index;
            this.performTease(reelId, stackedSymbolIndex, this._steps[reelId], stackedSpinTriggered);
            return true;
        }
        return false;
    }

    protected initAnimations():void {
        this._resourcesLoaded = true;
        super.initAnimations && super.initAnimations();
        for(let i:number = 0; i < BarbarianFuryGameConfig.instance.REELS_NUM; i++) {
            this.createFreezeAnimation(i);
        }
        //  added this check to only show bars when there is restre & gamemode is respin
        if(this._isRestore && (this._data.nextMode === BarbarianFuryGameMode.STACKED_RESPIN || this._data.nextMode === BarbarianFuryGameMode.STACKED_RESPIN_FREESPIN)) {
            this.showBarsOnRestore(this._restoreBardata);
        }
    }

    protected createFreezeAnimation(reelId:number):void {
        let symPos = Helper.getSymbolPositions(reelId, 2);
        let freezingSymAnim = new TimelineSprite(GameResources.getTextures("midSymAddFreezeAnim"), 20);
        let freezingSmokeAnim = new TimelineSprite(GameResources.getTextures("midSymAddSmokeAnim"), 24);
        let removeFreezingSymAnim = new TimelineSprite(GameResources.getTextures("midSymRemoveFreezeAnim"), 24);

        freezingSymAnim.anchor.set(0.5);
        freezingSymAnim.position.set(symPos[0], (symPos[1] - (BarbarianFuryGameConfig.instance.SYMBOL_HEIGHT / 2)));
        freezingSymAnim.visible = false;

        freezingSmokeAnim.anchor.set(0.5);
        freezingSmokeAnim.blendMode = PIXI.BLEND_MODES.ADD;
        freezingSmokeAnim.position.set(symPos[0], ((BarbarianFuryGameConfig.instance.SYMBOL_HEIGHT * 4) + BarbarianFuryGameConfig.instance.SYMBOL_HEIGHT / 2));
        freezingSmokeAnim.visible = false;

        removeFreezingSymAnim.anchor.set(0.5);
        removeFreezingSymAnim.position.set(symPos[0], (symPos[1] - (BarbarianFuryGameConfig.instance.SYMBOL_HEIGHT / 2)));
        removeFreezingSymAnim.visible = false;

        this._freezeAnims.push(freezingSymAnim);
        this._freezeSmokeAnims.push(freezingSmokeAnim);
        this._removingFreezeAnims.push(removeFreezingSymAnim);

        for(let i:number = 0; i < this._freezeAnims.length; i++) {
            this._frozenSymLayer.addChild(this._freezeAnims[i]);
            this._frozenSymLayer.addChild(this._freezeSmokeAnims[i]);
            this._frozenSymLayer.addChild(this._removingFreezeAnims[i]);
        }
    }
}